// ─── Wall State & Post Calculation Engine ────────────────────────────────────
// Manages which sides are attached to a building wall, derives structure type,
// and calculates minimum post positions using R-Blade chatflow logic.
// Supports: add/remove optional posts, cantilever (up to 36" inset from corner),
// and span rules (23' standard, 30' with structural header beam).

const MAX_SPAN_STANDARD_IN = 23 * 12;   // 23' — max unsupported span (standard)
const MAX_SPAN_HEADER_IN = 30 * 12;     // 30' — max span WITH structural header beam
const MAX_CANTILEVER_IN = 36;           // 3' max cantilever inset from corner

// Corner indices:  0 = front-left, 1 = front-right, 2 = back-right, 3 = back-left
// Edge definitions: each edge connects two corners and runs along a dimension
const EDGES = [
  { side: 'front', corners: [0, 1], spanKey: 'width' },
  { side: 'back',  corners: [3, 2], spanKey: 'width' },
  { side: 'left',  corners: [0, 3], spanKey: 'length' },
  { side: 'right', corners: [1, 2], spanKey: 'length' },
];

// Which two edges meet at each corner
const CORNER_EDGES = [
  ['front', 'left'],   // corner 0
  ['front', 'right'],  // corner 1
  ['back', 'right'],   // corner 2
  ['back', 'left'],    // corner 3
];

const STRUCTURE_LABELS = {
  0: 'Freestanding',
  1: 'Attached (1 side)',
  2: 'Attached (2 sides)',
  3: 'Attached (3 sides)',
};

let nextPostId = 1000;

export class WallManager {
  constructor() {
    this.walls = { front: false, back: false, left: false, right: false };
    this.centerPostToggles = { widthSide: false, lengthSide: false };
    this.structureType = 'Freestanding';
    this.wallCount = 0;
    this.postPositions = [];    // {id, x, z, type, edge?, cornerIndex?, removable, cantileverIn}
    this.minPostCount = 0;

    // User-added optional posts (e.g., center posts between zones)
    this.userPosts = [];        // {id, x, z, label}

    // Per-corner cantilever offsets (0 = at corner, up to 36" inset)
    this.cantilever = { 0: 0, 1: 0, 2: 0, 3: 0 };

    // Whether structural header beam is present (allows 30' spans)
    this.hasStructuralHeader = false;

    // Validation warnings/errors
    this.warnings = [];
  }

  setWall(side, enabled) {
    if (!['front', 'back', 'left', 'right'].includes(side)) return;

    // Enforce max 3 walls
    if (enabled) {
      const currentCount = Object.values(this.walls).filter(Boolean).length;
      if (currentCount >= 3 && !this.walls[side]) return;
    }

    this.walls[side] = enabled;
  }

  setWalls(wallState) {
    for (const side of ['front', 'back', 'left', 'right']) {
      if (side in wallState) {
        this.walls[side] = wallState[side];
      }
    }
  }

  // ─── Structural header beam flag (allows 30' spans) ─────────────
  setHasStructuralHeader(hasHeader) {
    this.hasStructuralHeader = hasHeader;
  }

  // ─── Cantilever: inset a corner post up to 36" ─────────────────
  setCantilever(cornerIndex, inchesInset) {
    if (cornerIndex < 0 || cornerIndex > 3) return;
    this.cantilever[cornerIndex] = Math.max(0, Math.min(MAX_CANTILEVER_IN, inchesInset));
  }

  getCantilever(cornerIndex) {
    return this.cantilever[cornerIndex] || 0;
  }

  // ─── Center post toggles (paired edges) ────────────────────────
  setCenterPostToggle(axis, enabled) {
    if (axis in this.centerPostToggles) {
      this.centerPostToggles[axis] = enabled;
    }
  }

  getCenterPostToggles() {
    return { ...this.centerPostToggles };
  }

  // ─── User-added posts ──────────────────────────────────────────
  addUserPost(xInches, zInches, label = 'Custom') {
    const post = {
      id: nextPostId++,
      x: xInches,
      z: zInches,
      label,
      type: 'user',
    };
    this.userPosts.push(post);
    return post;
  }

  removeUserPost(id) {
    this.userPosts = this.userPosts.filter(p => p.id !== id);
  }

  getUserPosts() {
    return [...this.userPosts];
  }

  // ─── Remove a system post (only if span rules allow) ───────────
  // Returns { removed: true } or { removed: false, reason: string }
  canRemovePost(postId) {
    const post = this.postPositions.find(p => p.id === postId);
    if (!post) return { canRemove: false, reason: 'Post not found' };
    if (!post.removable) return { canRemove: false, reason: 'This post is structurally required' };
    return { canRemove: true };
  }

  // ─── Core recalculation ─────────────────────────────────────────────────
  recalculate(widthInches, lengthInches) {
    const dims = { width: widthInches, length: lengthInches };
    const maxSpan = this.hasStructuralHeader ? MAX_SPAN_HEADER_IN : MAX_SPAN_STANDARD_IN;

    // 1. Structure type
    this.wallCount = Object.values(this.walls).filter(Boolean).length;
    this.structureType = STRUCTURE_LABELS[this.wallCount] || 'Freestanding';

    // 2. Determine which corners need posts
    const cornerNeedsPost = CORNER_EDGES.map(([edgeA, edgeB]) => {
      return !this.walls[edgeA] || !this.walls[edgeB];
    });

    // 3. Build corner positions (in inches, centered at origin)
    const halfW = widthInches / 2;
    const halfL = lengthInches / 2;

    const cornerCoords = [
      { x: -halfW, z: -halfL },  // 0: front-left
      { x: halfW,  z: -halfL },  // 1: front-right
      { x: halfW,  z: halfL },   // 2: back-right
      { x: -halfW, z: halfL },   // 3: back-left
    ];

    const positions = [];
    this.warnings = [];

    // Add corner posts (with cantilever offset if set)
    for (let i = 0; i < 4; i++) {
      if (!cornerNeedsPost[i]) continue;

      const cantIn = this.cantilever[i] || 0;
      let { x, z } = cornerCoords[i];

      // Cantilever: move post inward from corner by cantIn inches
      if (cantIn > 0) {
        const xDir = x < 0 ? 1 : -1;
        const zDir = z < 0 ? 1 : -1;
        // Move along both axes (inward) — split cantilever evenly or along longest edge
        // For simplicity: cantilever shifts along the width (X) axis
        x += xDir * cantIn;
      }

      positions.push({
        id: nextPostId++,
        x,
        z,
        type: 'corner',
        cornerIndex: i,
        cantileverIn: cantIn,
        removable: false, // corner posts cannot be removed
      });
    }

    // 4. Intermediate posts on open edges with long spans
    for (const edge of EDGES) {
      if (this.walls[edge.side]) continue; // skip walled edges

      const span = dims[edge.spanKey];

      // Check if span exceeds max
      if (span > MAX_SPAN_HEADER_IN) {
        this.warnings.push(
          `${edge.side} edge span (${(span / 12).toFixed(0)}') exceeds maximum ${(MAX_SPAN_HEADER_IN / 12).toFixed(0)}' even with structural header.`
        );
      } else if (span > MAX_SPAN_STANDARD_IN && !this.hasStructuralHeader) {
        this.warnings.push(
          `${edge.side} edge span (${(span / 12).toFixed(0)}') exceeds ${(MAX_SPAN_STANDARD_IN / 12).toFixed(0)}'. Add a structural header beam to support up to ${(MAX_SPAN_HEADER_IN / 12).toFixed(0)}'.`
        );
      }

      const segments = Math.ceil(span / maxSpan);

      if (segments > 1) {
        const [c0, c1] = edge.corners;
        const p0 = cornerCoords[c0];
        const p1 = cornerCoords[c1];

        for (let i = 1; i < segments; i++) {
          const t = i / segments;
          positions.push({
            id: nextPostId++,
            x: p0.x + (p1.x - p0.x) * t,
            z: p0.z + (p1.z - p0.z) * t,
            type: 'intermediate',
            edge: edge.side,
            // Intermediate posts CAN be removed IF the remaining span is within limits
            removable: this._canRemoveIntermediatePost(span, segments, maxSpan),
          });
        }
      }
    }

    // 4b. Center post toggles — add midpoint posts on paired edges
    const hasPostNear = (x, z) =>
      positions.some(p => Math.abs(p.x - x) < 1 && Math.abs(p.z - z) < 1);

    if (this.centerPostToggles.widthSide) {
      if (!this.walls.front && !hasPostNear(0, -halfL)) {
        positions.push({ id: nextPostId++, x: 0, z: -halfL, type: 'center', edge: 'front', removable: true });
      }
      if (!this.walls.back && !hasPostNear(0, halfL)) {
        positions.push({ id: nextPostId++, x: 0, z: halfL, type: 'center', edge: 'back', removable: true });
      }
    }
    if (this.centerPostToggles.lengthSide) {
      if (!this.walls.left && !hasPostNear(-halfW, 0)) {
        positions.push({ id: nextPostId++, x: -halfW, z: 0, type: 'center', edge: 'left', removable: true });
      }
      if (!this.walls.right && !hasPostNear(halfW, 0)) {
        positions.push({ id: nextPostId++, x: halfW, z: 0, type: 'center', edge: 'right', removable: true });
      }
    }

    // 5. Add user-added posts
    for (const up of this.userPosts) {
      positions.push({
        id: up.id,
        x: up.x,
        z: up.z,
        type: 'user',
        label: up.label,
        removable: true,
      });
    }

    this.postPositions = positions;
    this.minPostCount = positions.length;
  }

  _canRemoveIntermediatePost(totalSpan, currentSegments, maxSpan) {
    // If removing one intermediate post, the resulting segments must still be within max span
    const newSegments = currentSegments - 1;
    if (newSegments < 1) return false;
    const newMaxSegmentSpan = totalSpan / newSegments;
    return newMaxSegmentSpan <= maxSpan;
  }

  getStructureType() {
    return this.structureType;
  }

  getPostCount() {
    return this.minPostCount;
  }

  getOpenEdges() {
    return EDGES.filter(e => !this.walls[e.side]);
  }

  getWalledSides() {
    return Object.entries(this.walls)
      .filter(([, v]) => v)
      .map(([k]) => k);
  }

  getWarnings() {
    return [...this.warnings];
  }

  getBayCount(widthInches, lengthInches) {
    const widthFt = widthInches / 12;
    const lengthFt = lengthInches / 12;
    const widthBays = Math.ceil(widthFt / 16);
    const lengthBays = Math.ceil(lengthFt / 23);
    return {
      widthBays,
      lengthBays,
      totalBays: widthBays * lengthBays,
    };
  }

  // ─── Multi-Zone Recalculation ─────────────────────────────────────────────
  // zoneOffsets: array of { centerX, centerZ, widthInches, lengthInches } per zone
  // isWidthAxis: true if zones stack along X, false if along Z
  recalculateMultiZone(totalWidthInches, totalLengthInches, zoneOffsets, isWidthAxis) {
    // Set up structure type and warnings using overall footprint
    const dims = { width: totalWidthInches, length: totalLengthInches };
    const maxSpan = this.hasStructuralHeader ? MAX_SPAN_HEADER_IN : MAX_SPAN_STANDARD_IN;
    this.wallCount = Object.values(this.walls).filter(Boolean).length;
    this.structureType = STRUCTURE_LABELS[this.wallCount] || 'Freestanding';
    this.warnings = [];

    const positions = [];

    // Collect unique post positions from all zone corners
    // Each zone has 4 corners; shared corners at junctions get one post
    const postMap = new Map(); // key "x,z" → post data

    const addPost = (x, z, type, edge = null) => {
      const key = `${Math.round(x)},${Math.round(z)}`;
      if (!postMap.has(key)) {
        postMap.set(key, {
          id: nextPostId++,
          x, z, type, edge,
          removable: type === 'junction',
        });
      }
    };

    for (let i = 0; i < zoneOffsets.length; i++) {
      const off = zoneOffsets[i];
      const halfW = off.widthInches / 2;
      const halfL = off.lengthInches / 2;
      const cx = off.centerX || 0;
      const cz = off.centerZ || 0;

      // 4 corners of this zone
      const corners = [
        { x: cx - halfW, z: cz - halfL, edges: ['front', 'left'] },
        { x: cx + halfW, z: cz - halfL, edges: ['front', 'right'] },
        { x: cx + halfW, z: cz + halfL, edges: ['back', 'right'] },
        { x: cx - halfW, z: cz + halfL, edges: ['back', 'left'] },
      ];

      for (const c of corners) {
        // Determine if this corner is on an outer edge or a junction
        const isOuterFront = Math.abs(c.z - (-totalLengthInches / 2)) < 1;
        const isOuterBack = Math.abs(c.z - (totalLengthInches / 2)) < 1;
        const isOuterLeft = Math.abs(c.x - (-totalWidthInches / 2)) < 1;
        const isOuterRight = Math.abs(c.x - (totalWidthInches / 2)) < 1;
        const isOuter = isOuterFront || isOuterBack || isOuterLeft || isOuterRight;

        // Skip if corner is on a walled edge (both edges walled = no post)
        const edge0walled = this.walls[c.edges[0]];
        const edge1walled = this.walls[c.edges[1]];
        if (isOuter && edge0walled && edge1walled) continue;

        const type = isOuter ? 'corner' : 'junction';
        addPost(c.x, c.z, type);
      }
    }

    // Convert map to positions array
    for (const post of postMap.values()) {
      if (post.type === 'corner') post.removable = false;
      positions.push(post);
    }

    // Check for span warnings on outer edges
    const halfTotalW = totalWidthInches / 2;
    const halfTotalL = totalLengthInches / 2;
    for (const edge of EDGES) {
      if (this.walls[edge.side]) continue;
      const span = dims[edge.spanKey];
      if (span > MAX_SPAN_HEADER_IN) {
        this.warnings.push(
          `${edge.side} edge span (${(span / 12).toFixed(0)}') exceeds maximum ${(MAX_SPAN_HEADER_IN / 12).toFixed(0)}'.`
        );
      } else if (span > MAX_SPAN_STANDARD_IN && !this.hasStructuralHeader) {
        this.warnings.push(
          `${edge.side} edge span (${(span / 12).toFixed(0)}') exceeds ${(MAX_SPAN_STANDARD_IN / 12).toFixed(0)}'. Add a structural header.`
        );
      }
    }

    // Add center post toggles
    const hasPostNear = (x, z) =>
      positions.some(p => Math.abs(p.x - x) < 1 && Math.abs(p.z - z) < 1);

    if (this.centerPostToggles.widthSide) {
      if (!this.walls.front && !hasPostNear(0, -halfTotalL)) {
        positions.push({ id: nextPostId++, x: 0, z: -halfTotalL, type: 'center', edge: 'front', removable: true });
      }
      if (!this.walls.back && !hasPostNear(0, halfTotalL)) {
        positions.push({ id: nextPostId++, x: 0, z: halfTotalL, type: 'center', edge: 'back', removable: true });
      }
    }
    if (this.centerPostToggles.lengthSide) {
      if (!this.walls.left && !hasPostNear(-halfTotalW, 0)) {
        positions.push({ id: nextPostId++, x: -halfTotalW, z: 0, type: 'center', edge: 'left', removable: true });
      }
      if (!this.walls.right && !hasPostNear(halfTotalW, 0)) {
        positions.push({ id: nextPostId++, x: halfTotalW, z: 0, type: 'center', edge: 'right', removable: true });
      }
    }

    // Add user posts
    for (const up of this.userPosts) {
      positions.push({
        id: up.id, x: up.x, z: up.z,
        type: 'user', label: up.label, removable: true,
      });
    }

    this.postPositions = positions;
    this.minPostCount = positions.length;
  }

  getJunctionPostCount() {
    return this.postPositions.filter(p => p.type === 'junction').length;
  }

  getCornerPostCount() {
    return this.postPositions.filter(p => p.type === 'corner').length;
  }

  getUserPostCount() {
    return this.postPositions.filter(p => p.type === 'user').length;
  }

  getCenterPostCount() {
    return this.postPositions.filter(p => p.type === 'center').length;
  }

  getRemovablePosts() {
    return this.postPositions.filter(p => p.removable);
  }
}
